import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, InputAdornment, IconButton, CircularProgress, Alert
} from '@mui/material';
import { Visibility, VisibilityOff, LockOpen, Lock, VpnKey } from '@mui/icons-material';
import { changeAdminPassword } from '../../services/Api';

const UpdatePassword = ({ open, onClose }) => {
    const [formData, setFormData] = useState({ oldPassword: '', newPassword: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleClose = () => {
        setFormData({ oldPassword: '', newPassword: '' });
        setError(null);
        setSuccess(null);
        onClose();
    };

    const handleSubmit = async () => {
        if (!formData.oldPassword || !formData.newPassword) {
            setError("Both fields are required.");
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await changeAdminPassword(formData);
            if (response.success || response.message) {
                setSuccess(response.message || "Password updated successfully!");
                setTimeout(() => {
                    handleClose();
                }, 2000);
            } else {
                setError(response?.message || "Failed to update password");
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || "An error occurred while updating the password.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VpnKey color="primary" /> Update Password
            </DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2, mt: 1 }}>{success}</Alert>}

                <TextField
                    fullWidth
                    margin="normal"
                    label="Current Password"
                    name="oldPassword"
                    type={showOldPassword ? 'text' : 'password'}
                    value={formData.oldPassword}
                    onChange={handleChange}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><LockOpen color="action" /></InputAdornment>,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowOldPassword(!showOldPassword)} edge="end">
                                    {showOldPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                
                <TextField
                    fullWidth
                    margin="normal"
                    label="New Password"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={formData.newPassword}
                    onChange={handleChange}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Lock color="action" /></InputAdornment>,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={handleClose} color="inherit" disabled={loading}>Cancel</Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    color="primary" 
                    disabled={loading}
                    sx={{ borderRadius: '8px' }}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UpdatePassword;
